import { ReactNode, useEffect, useMemo } from 'react'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: barra de assinatura em lote). */
  actions?: ReactNode
}

export const SignAsoListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
        debouncedFilters.estado ? `estado.id:${debouncedFilters.estado.value}` : '',
        debouncedFilters.paciente
          ? `paciente.pessoa.primeiroNome~*${debouncedFilters.paciente.trim()}*`
          : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.estado && {
      key: 'estado',
      label: t('cols.estado'),
      value: filters.estado.label,
      onRemove: () => setFilter('estado', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'ASO para assinar' : 'ASOs para assinar'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.estado')}>
        <CustomSelect
          placeholder='Selecione'
          onChange={(estado?: IOption<number>) => setFilter('estado', estado ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEstadosAtendimento}
          value={filters.estado}
        />
      </TableFilter>
    </TableFilters>
  )
}
